<template>
  <div>
    <a-modal title="业务需求报价确认" :visible="visible" :width="600" @cancel="onCancel" @ok="onOk">
      <template slot="footer">
        <a-button @click="onCancel">取消</a-button>
        <a-button type="primary" :loading="saveLoading" @click="onOk">保存</a-button>
      </template>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <a-spin size="large">
          <div class="loading-text">正在加载评估数据...</div>
        </a-spin>
      </div>

      <!-- 主要内容 -->
      <div v-else class="assess-confirm-content">
        <!-- 团队评估记录 -->
        <div v-if="false" class="team-assess-section">
          <div class="section-header">
            <a-icon type="team" class="header-icon" />
            <span class="header-text">团队评估记录</span>
          </div>

          <div v-if="assessTaskList.length > 0" class="team-table">
            <a-table :columns="teamColumns" :data-source="assessTaskList" :pagination="false" size="middle" :show-header="true">
              <template slot="brPoint" slot-scope="text, record">
                <span class="point-value" :class="{ 'valid-point': record.brPoint > 0, 'invalid-point': !record.brPoint }"> {{ record.brPoint || 0 }}点 </span>
              </template>
            </a-table>
          </div>

          <div v-else class="empty-state">
            <a-empty description="暂无评估记录" size="small" />
          </div>
        </div>

        <!-- 报价确认 -->
        <div class="workload-confirm-section">
          <div class="section-header">
            <a-icon type="calculator" class="header-icon" />
            <span class="header-text">报价确认</span>
          </div>

          <div class="confirm-content">
            <!-- 计算方式选择 -->
            <div class="method-selection">
              <span class="method-label">计算方式：</span>
              <a-radio-group v-model="calculationMethod" button-style="solid" @change="onCalculationMethodChange">
                <a-radio-button value="min">最小值</a-radio-button>
                <a-radio-button value="avg">平均数</a-radio-button>
                <a-radio-button value="median">中位数</a-radio-button>
              </a-radio-group>
            </div>

            <!-- 报价输入 -->
            <div class="workload-input-section">
              <div class="input-group">
                <span class="input-label">最终报价：</span>
                <a-input-number v-model="finalWorkload" :min="minWorkloadLimit" :max="maxWorkloadLimit" :precision="0" :placeholder="`系统计算: ${calculatedResult}点`" class="workload-input" />
                <span class="input-unit">BRP({{ virtualCoin }}虚拟币)</span>
              </div>
              <div class="calculation-info">
                <span class="info-text">如果手动调整最终报价, 不能超出团队最高报价的50%或低于最低报价的50%</span>
              </div>
              <div v-if="false" class="calculation-info">
                <span class="info-text">{{ getCalculationDescription() }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>

    <a-button v-has="{ code: 'business:venue:edit' }" type="primary" @click="onOpen">报价确认</a-button>
  </div>
</template>

<script>
import { assessTaskQuery } from '@/api/business/assess';
import { updateBusinessRequirementSimpleField } from '@/api/business/requirement';

export default {
  name: 'BrAssessConfirmModal',
  props: {
    brId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      maxPoint: 0,
      saveLoading: false,
      assessTaskList: [],
      calculationMethod: 'avg', // 默认平均数
      calculatedResult: 0,
      finalWorkload: undefined,
      maxWorkloadLimit: 9999, // 最大报价限制
      minWorkloadLimit: 1, // 最小报价限制
      teamColumns: [
        {
          title: '团队名称',
          dataIndex: 'teamName',
          key: 'teamName',
          width: '60%'
        },
        {
          title: '评估点数',
          dataIndex: 'brPoint',
          key: 'brPoint',
          width: '40%',
          scopedSlots: { customRender: 'brPoint' }
        }
      ]
    };
  },

  computed: {
    businessAssessConfig() {
      return this.$store.getters.globalConfig('business_assess', {
        price: 0,
        avg_point: 1,
        commission: 0,
        task_point_ratio: 0
      });
    },
    virtualCoin() {
      return parseInt((((this.finalWorkload || 0) * this.businessAssessConfig.task_point_ratio) / this.businessAssessConfig.avg_point) * this.businessAssessConfig.price * (1 + this.businessAssessConfig.commission));
    }
  },

  watch: {
    assessTaskList: {
      handler() {
        this.calculateResult();
      },
      deep: true
    },
    calculatedResult(newVal) {
      // 当计算结果更新时，自动更新最终报价
      if (newVal > 0 && this.finalWorkload === undefined) {
        this.finalWorkload = newVal;
      }
    }
  },
  beforeDestroy() {
    this.resetData();
  },
  methods: {
    /**
     * 打开弹窗并加载数据
     */
    onOpen() {
      this.visible = true;
      this.getAssessTaskList();
    },

    /**
     * 获取评估任务列表
     */
    getAssessTaskList() {
      this.loading = true;
      assessTaskQuery({
        brId: this.brId
      })
        .then(res => {
          this.assessTaskList = res.data.items.map(item => ({
            ...item,
            brPoint: item.brPoint || 0
          }));

          // 计算报价限制
          this.calculateWorkloadLimits();

          this.calculateResult();
          // 使用nextTick确保计算完成后再设置最终报价
          this.$nextTick(() => {
            if (this.calculatedResult > 0) {
              this.finalWorkload = this.calculatedResult;
            }
          });
        })
        .catch(error => {
          console.error('获取评估任务失败:', error);
          this.$message.error('获取评估数据失败，请重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * 计算报价限制
     */
    calculateWorkloadLimits() {
      if (!this.assessTaskList || this.assessTaskList.length === 0) {
        this.maxWorkloadLimit = 9999;
        this.minWorkloadLimit = 1;
        return;
      }

      // 获取所有有效的点数
      const points = this.assessTaskList.map(item => item.brPoint || 0).filter(point => point > 0);

      if (points.length === 0) {
        this.maxWorkloadLimit = 9999;
        this.minWorkloadLimit = 1;
        return;
      }

      const maxPoint = Math.max(...points);
      const minPoint = Math.min(...points);

      this.maxWorkloadLimit = Math.floor(maxPoint * 1.5); // 最大值的150%，向下取整
      this.minWorkloadLimit = Math.ceil(minPoint * 0.5); // 最小值的50%，向上取整
    },

    /**
     * 计算综合报价
     */
    calculateResult() {
      if (!this.assessTaskList || this.assessTaskList.length === 0) {
        this.calculatedResult = 0;
        return;
      }

      // 获取所有有效的点数
      const points = this.assessTaskList.map(item => item.brPoint || 0).filter(point => point > 0);

      if (points.length === 0) {
        this.calculatedResult = 0;
        return;
      }

      let result = 0;

      switch (this.calculationMethod) {
        case 'min': {
          result = Math.min(...points);
          break;
        }
        case 'avg': {
          const sum = points.reduce((acc, point) => acc + point, 0);
          result = Math.ceil(sum / points.length); // 向上取整
          break;
        }
        case 'median': {
          const sortedPoints = [...points].sort((a, b) => a - b);
          const mid = Math.floor(sortedPoints.length / 2);
          if (sortedPoints.length % 2 === 0) {
            result = Math.ceil((sortedPoints[mid - 1] + sortedPoints[mid]) / 2);
          } else {
            result = sortedPoints[mid];
          }
          break;
        }
      }

      this.calculatedResult = result;
    },

    /**
     * 计算方式改变时触发
     */
    onCalculationMethodChange() {
      this.calculateResult();
      // 计算方式改变时，自动更新最终报价为新的计算结果
      if (this.calculatedResult > 0) {
        this.finalWorkload = this.calculatedResult;
      }
    },

    /**
     * 获取计算方式描述
     */
    getCalculationDescription() {
      const validPoints = this.assessTaskList.map(item => item.brPoint || 0).filter(point => point > 0);

      if (validPoints.length === 0) {
        return '暂无有效评估数据';
      }

      const pointsStr = validPoints.join('、');

      switch (this.calculationMethod) {
        case 'min':
          return `取 ${pointsStr} 中的最小值`;
        case 'avg': {
          const sum = validPoints.reduce((acc, point) => acc + point, 0);
          return `(${pointsStr}) ÷ ${validPoints.length} = ${(sum / validPoints.length).toFixed(2)}，向上取整为 ${this.calculatedResult}`;
        }
        case 'median': {
          const sorted = [...validPoints].sort((a, b) => a - b);
          if (sorted.length % 2 === 0) {
            const mid1 = sorted[Math.floor(sorted.length / 2) - 1];
            const mid2 = sorted[Math.floor(sorted.length / 2)];
            return `排序后 ${sorted.join('、')}，取中间两个数 (${mid1} + ${mid2}) ÷ 2 的中位数`;
          } else {
            return `排序后 ${sorted.join('、')}，取中间数 ${sorted[Math.floor(sorted.length / 2)]}`;
          }
        }
        default:
          return '';
      }
    },

    /**
     * 取消操作
     */
    onCancel() {
      this.visible = false;
      this.resetData();
    },

    /**
     * 保存确认结果
     */
    onOk() {
      // 表单验证
      if (this.finalWorkload === undefined || this.finalWorkload === null || this.finalWorkload === '') {
        this.$message.warning('请输入最终确认的报价');
        return;
      }

      if (this.finalWorkload < this.minWorkloadLimit) {
        this.$message.warning(`报价不能小于${this.minWorkloadLimit}点（评估记录最小值的50%）`);
        return;
      }

      if (this.finalWorkload > this.maxWorkloadLimit) {
        this.$message.warning(`报价不能超过${this.maxWorkloadLimit}点（评估记录最大值的150%）`);
        return;
      }

      this.saveLoading = true;
      updateBusinessRequirementSimpleField({
        id: this.brId,
        fields: {
          point: this.finalWorkload
        }
      })
        .then(() => {
          this.$message.success('报价确认保存成功');
          this.visible = false;
          this.resetData();
          this.$emit('ok');
        })
        .catch(error => {
          console.error('保存失败:', error);
          this.$message.error('保存失败');
        })
        .finally(() => {
          this.saveLoading = false;
        });
    },

    /**
     * 重置数据
     */
    resetData() {
      this.assessTaskList = [];
      this.loading = false;
      this.saveLoading = false;
      this.calculatedResult = 0;
      this.finalWorkload = undefined;
      this.calculationMethod = 'avg';
      this.maxWorkloadLimit = 9999;
      this.minWorkloadLimit = 1;
    }
  }
};
</script>

<style lang="less" scoped>
.loading-container {
  text-align: center;
  padding: 40px 20px;

  .loading-text {
    margin-top: 16px;
    color: #86909c;
    font-size: 14px;
  }
}

.assess-confirm-content {
  // 通用区域头部样式
  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;

    .header-icon {
      margin-right: 8px;
      font-size: 16px;
    }

    .header-text {
      font-size: 14px;
      font-weight: 500;
      color: #1f2329;
    }
  }

  // 团队评估记录表格
  .team-assess-section {
    margin-bottom: 12px;
    padding: 12px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #d9d9d9;

    .section-header .header-icon {
      color: #1890ff;
    }

    .team-table {
      margin-top: 16px;

      .point-value {
        font-weight: 500;

        &.valid-point {
          color: #52c41a;
        }

        &.invalid-point {
          color: #999;
        }
      }
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
    }
  }

  // 报价确认区域
  .workload-confirm-section {
    padding: 12px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #d9d9d9;

    .section-header .header-icon {
      color: #52c41a;
    }

    .confirm-content {
      .method-selection {
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 12px;

        .method-label {
          font-size: 14px;
          color: #666;
          font-weight: 500;
          width: 100px;
          text-align: right;
        }
      }

      .workload-input-section {
        .input-group {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;

          .input-label {
            font-size: 14px;
            color: #666;
            font-weight: 500;
            width: 100px;
            text-align: right;
          }

          .workload-input {
            width: 140px;
          }

          .input-unit {
            font-size: 14px;
            color: #999;
          }
        }

        .calculation-info {
          .info-text {
            font-size: 12px;
            color: #999;
            line-height: 1.4;
          }
        }
      }
    }
  }
}
</style>
